import {
  TextNode,
  SerializedTextNode,
  LexicalNode,
  NodeKey,
  $applyNodeReplacement,
  EditorConfig,
  DOMConversionMap,
  DOMConversionOutput,
  Spread,
} from 'lexical';

export type SerializedWhatsAppBoldNode = Spread<
  {
    type: 'whatsapp-bold';
  },
  SerializedTextNode
>;

/**
 * WhatsApp Bold Node
 * Uses single asterisk (*text*) instead of standard markdown double asterisk
 */
export class WhatsAppBoldNode extends TextNode {
  static getType(): string {
    return 'whatsapp-bold';
  }

  static clone(node: WhatsAppBoldNode): WhatsAppBoldNode {
    return new WhatsAppBoldNode(node.__text, node.__key);
  }



  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(text: string, key?: NodeKey) {
    super(text, key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement('strong');
    element.className = config.theme.whatsappBold || '';
    element.textContent = this.__text;
    return element;
  }

  updateDOM(
    prevNode: WhatsAppBoldNode,
    dom: HTMLElement,
    config: EditorConfig
  ): boolean {
    // If the text content has changed, update the DOM
    const prevText = prevNode.__text;
    const currentText = this.__text;
    
    if (prevText !== currentText) {
      dom.textContent = currentText;
    }
    
    // Return false because we handle the update ourselves
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      strong: () => ({
        conversion: convertBoldElement,
        priority: 1,
      }),
      b: () => ({
        conversion: convertBoldElement,
        priority: 1,
      }),
    };
  }

  static importJSON(serializedNode: SerializedWhatsAppBoldNode): WhatsAppBoldNode {
    const node = $createWhatsAppBoldNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON(): SerializedWhatsAppBoldNode {
    return {
      ...super.exportJSON(),
      type: 'whatsapp-bold',
    };
  }

  // Custom export for WhatsApp markdown
  exportMarkdown(): string {
    return `*${this.__text}*`;
  }
}

function convertBoldElement(): DOMConversionOutput {
  return {
    node: null,
  };
}

export function $createWhatsAppBoldNode(text: string): WhatsAppBoldNode {
  return $applyNodeReplacement(new WhatsAppBoldNode(text));
}

export function $isWhatsAppBoldNode(
  node: LexicalNode | null | undefined
): node is WhatsAppBoldNode {
  return node instanceof WhatsAppBoldNode;
}
